import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import AccessRestricted from "../../components/AccessRestricted";
import api from "../../utils/api";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Users,
  AlertCircle,
  CheckCircle2,
  Search,
  Eye,
  LayoutTemplate,
  Settings,
  User,
  UserCheck,
  Clock,
  Loader2,
  XCircle,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Send,
  RotateCcw,
} from "lucide-react";
import { toast } from "sonner";

interface OnboardingStats {
  totalRequests: number;
  notStarted: number;
  inProgress: number;
  completed: number;
}

interface OnboardingRequest {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  startDate: string | null;
  progress: number;
  status: "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED" | "CANCELLED" | "CONVERTED";
  formsStatus: string | null;
  policiesStatus: string | null;
  trainingStatus: string | null;
  // Resolved server-side from the hire form (requisition/job position).
  jobRoleName: string | null;
  managerName: string | null;
  jobPosition: {
    jobRole: {
      roleName: string;
    } | null;
  } | null;
  manager: {
    id: number;
    name: string;
  } | null;
  createdAt: string;
}

interface FilterOptions {
  statuses: string[];
  departments: string[];
  requesters: { id: number; name: string }[];
}

// Logical lifecycle order used when sorting the list by status.
const STATUS_SORT_ORDER: Record<OnboardingRequest["status"], number> = {
  NOT_STARTED: 0,
  IN_PROGRESS: 1,
  COMPLETED: 2,
  CONVERTED: 3,
  CANCELLED: 4,
};

const OnboardingDashboard: React.FC = () => {
  const ability = useAbility(AbilityContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();

  const [stats, setStats] = useState<OnboardingStats | null>(null);
  const [requests, setRequests] = useState<OnboardingRequest[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<OnboardingRequest[]>([]);
  const [filters, setFilters] = useState<FilterOptions | null>(null);
  const [loading, setLoading] = useState(true);

  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Statuses");
  const [departmentFilter, setDepartmentFilter] = useState("All Departments");
  const [requesterFilter, setRequesterFilter] = useState("All Requesters");
  const [viewMode, setViewMode] = useState<"table" | "cards">("table");
  const [statusSort, setStatusSort] = useState<"asc" | "desc" | null>(null);
  const [convertingId, setConvertingId] = useState<number | null>(null);
  const [cancellingId, setCancellingId] = useState<number | null>(null);
  const [resendingId, setResendingId] = useState<number | null>(null);
  const [restoringId, setRestoringId] = useState<number | null>(null);
  const [pendingCancel, setPendingCancel] = useState<OnboardingRequest | null>(null);

  // The dashboard's content is the list of employees in onboarding, so it
  // requires list access too — without it every data fetch on this page 403s
  // and the stats alone would leak headcounts to users who may not view them.
  const canViewList = ability.can(ACTIONS.READ, RESOURCES.ONBOARDING_REQUEST_LIST);
  const canViewDashboard =
    ability.can(ACTIONS.READ, RESOURCES.ONBOARDING_DASHBOARD) && canViewList;
  const canConvert = ability.can(ACTIONS.UPDATE, RESOURCES.ONBOARDING_REQUEST_STATUS);
  const canCancel = ability.can(ACTIONS.UPDATE, RESOURCES.ONBOARDING_REQUEST_STATUS);

  // Convert a completed hire to an active user (same action as the detail
  // page's "Convert to Active User" CTA). On success the request moves to
  // the final CONVERTED status.
  const handleConvertToUser = async (request: OnboardingRequest) => {
    try {
      setConvertingId(request.id);
      const res = await api.post<{ success: boolean; message: string }>(
        `/api/onboarding/${request.id}/convert`,
        {}
      );
      if (res.success) {
        toast.success(`${request.firstName} ${request.lastName} converted to active user`);
        setRequests((prev) =>
          prev.map((r) => (r.id === request.id ? { ...r, status: "CONVERTED" } : r))
        );
      } else {
        toast.error(res.message || "Failed to convert user");
      }
    } catch (error) {
      toast.error("Failed to convert user");
    } finally {
      setConvertingId(null);
    }
  };
  // Re-issue the hire's account-setup token and re-send the welcome email
  // (same action as the detail page's "Resend Onboarding Link" button) —
  // for hires whose link expired before they set a password.
  const handleResendInvite = async (request: OnboardingRequest) => {
    try {
      setResendingId(request.id);
      const res = await api.post<{ success: boolean; message: string }>(
        `/api/onboarding/${request.id}/resend-invite`,
        {}
      );
      if (res.success) {
        toast.success(
          res.message || `Onboarding link resent to ${request.firstName} ${request.lastName}`
        );
      } else {
        toast.error(res.message || "Failed to resend the onboarding link");
      }
    } catch (error) {
      // api.ts already toasts the server error; single toast only.
    } finally {
      setResendingId(null);
    }
  };
  // Restore a cancelled hire: un-archive the account (Cancel archives it,
  // which blocks login), re-open the request and re-send the onboarding link
  // in one step.
  const handleRestoreInvite = async (request: OnboardingRequest) => {
    try {
      setRestoringId(request.id);
      const res = await api.post<{ success: boolean; message: string }>(
        `/api/onboarding/${request.id}/restore-invite`,
        {}
      );
      if (res.success) {
        toast.success(
          res.message || `Onboarding restored for ${request.firstName} ${request.lastName}`
        );
        setRequests((prev) =>
          prev.map((r) => (r.id === request.id ? { ...r, status: "IN_PROGRESS" } : r))
        );
      } else {
        toast.error(res.message || "Failed to restore the onboarding request");
      }
    } catch (error) {
      // api.ts already toasts the server error; single toast only.
    } finally {
      setRestoringId(null);
    }
  };
  // Cancel a request: the backend archives the linked account (blocking
  // magic-link, account-setup and password login) and moves the request to
  // the terminal CANCELLED status.
  const handleCancel = async (request: OnboardingRequest) => {
    try {
      setCancellingId(request.id);
      const res = await api.post<{ success: boolean; message: string }>(
        `/api/onboarding/${request.id}/cancel`,
        {}
      );
      if (res.success) {
        toast.success(`Onboarding request for ${request.firstName} ${request.lastName} cancelled`);
        setRequests((prev) =>
          prev.map((r) => (r.id === request.id ? { ...r, status: "CANCELLED" } : r))
        );
      } else {
        toast.error(res.message || "Failed to cancel request");
      }
    } catch (error) {
      toast.error("Failed to cancel request");
    } finally {
      setCancellingId(null);
      setPendingCancel(null);
    }
  };

  // Refresh just the stats + request rows — the data that changes as hires
  // progress through their onboarding. Used for live polling/focus refetch;
  // filter options are static enough to only load with the page.
  const refreshRows = async () => {
    try {
      const [statsRes, requestsRes] = await Promise.all([
        api.get<{ success: boolean; stats: OnboardingStats }>("/api/onboarding/stats"),
        api.get<{ success: boolean; data: OnboardingRequest[] }>("/api/onboarding"),
      ]);
      if (statsRes.success) setStats(statsRes.stats);
      if (requestsRes.success) {
        setRequests(requestsRes.data);
        setFilteredRequests(requestsRes.data);
      }
    } catch (error) {
      console.error("Error refreshing onboarding data:", error);
    }
  };

  useEffect(() => {
    if (!canViewDashboard) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        const [statsRes, requestsRes, filtersRes] = await Promise.all([
          api.get<{ success: boolean; stats: OnboardingStats }>("/api/onboarding/stats"),
          api.get<{ success: boolean; data: OnboardingRequest[] }>("/api/onboarding"),
          api.get<{ success: boolean; filters: FilterOptions }>("/api/onboarding/filters"),
        ]);

        if (statsRes.success) setStats(statsRes.stats);
        if (requestsRes.success) {
          setRequests(requestsRes.data);
          setFilteredRequests(requestsRes.data);
        }
        if (filtersRes.success) setFilters(filtersRes.filters);
      } catch (error) {
        console.error("Error fetching onboarding data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [canViewDashboard, location.key]);

  // Keep the dashboard live while it sits open: poll every 30s and refetch
  // when the tab regains focus, so Progress %/status track the hires' portal
  // activity in near real time.
  useEffect(() => {
    if (!canViewDashboard) return;
    const interval = setInterval(refreshRows, 30000);
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") refreshRows();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [canViewDashboard]);

  // Apply filters
  useEffect(() => {
    let result = [...requests];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (r) =>
          r.firstName.toLowerCase().includes(query) ||
          r.lastName.toLowerCase().includes(query) ||
          r.email.toLowerCase().includes(query)
      );
    }

    if (statusFilter !== "All Statuses") {
      result = result.filter((r) => r.status === statusFilter);
    }

    if (statusSort) {
      result.sort((a, b) => {
        const diff = STATUS_SORT_ORDER[a.status] - STATUS_SORT_ORDER[b.status];
        return statusSort === "asc" ? diff : -diff;
      });
    }

    setFilteredRequests(result);
  }, [searchQuery, statusFilter, statusSort, requests]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "NOT_STARTED":
        return (
          <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">
            <Clock className="w-3 h-3 mr-1" />
            Not Started
          </Badge>
        );
      case "IN_PROGRESS":
        return (
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
            <AlertCircle className="w-3 h-3 mr-1" />
            In Progress
          </Badge>
        );
      case "COMPLETED":
        return (
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Completed
          </Badge>
        );
      case "CANCELLED":
        return (
          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
            Cancelled
          </Badge>
        );
      case "CONVERTED":
        return (
          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
            <UserCheck className="w-3 h-3 mr-1" />
            Converted to Active User
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getItemStatusBadge = (status: string | null) => {
    if (!status) return <span className="text-gray-400 text-sm">—</span>;

    const statusLower = status.toLowerCase();
    if (statusLower === "pending" || statusLower === "not started") {
      return (
        <Badge variant="outline" className="bg-gray-50 text-gray-600 border-gray-200 text-xs">
          {statusLower === "pending" ? "Pending" : "Not Started"}
        </Badge>
      );
    }
    if (statusLower === "in progress") {
      return (
        <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 text-xs">
          In Progress
        </Badge>
      );
    }
    if (statusLower === "completed" || statusLower === "done") {
      return (
        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
          Completed
        </Badge>
      );
    }
    return <Badge variant="outline" className="text-xs">{status}</Badge>;
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  // Cycle the Status column sort: ascending → descending → off.
  const toggleStatusSort = () => {
    setStatusSort((prev) => (prev === null ? "asc" : prev === "asc" ? "desc" : null));
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  if (!canViewDashboard) {
    return <AccessRestricted />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-6 animate-in fade-in duration-500 pb-10 px-0 sm:px-4 lg:px-0">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
            Onboarding Dashboard
          </h1>
          <p className="text-gray-500 text-sm font-medium">
            Manage and track staff onboarding requests across all departments
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" className="gap-2" onClick={() => navigate("/onboarding/templates")}>
            <LayoutTemplate className="w-4 h-4" />
            Templates
          </Button>
          <Button variant="outline" size="sm" className="gap-2" onClick={() => navigate("/onboarding/settings")}>
            <Settings className="w-4 h-4" />
            Settings
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-50 rounded-xl">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500 font-medium">Total Requests</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.totalRequests ?? 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gray-100 rounded-xl">
              <Clock className="w-6 h-6 text-gray-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500 font-medium">Not Started</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.notStarted ?? 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-amber-50 rounded-xl">
              <AlertCircle className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500 font-medium">In Progress</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.inProgress ?? 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-50 rounded-xl">
              <CheckCircle2 className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500 font-medium">Completed</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.completed ?? 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search requests..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-gray-50 border-gray-200"
            />
          </div>
          <div className="flex gap-3">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px] bg-gray-50 border-gray-200">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                {filters?.statuses.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status === "All Statuses" ? "All Statuses" : status.replace("_", " ")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
              <SelectTrigger className="w-[180px] bg-gray-50 border-gray-200">
                <SelectValue placeholder="All Departments" />
              </SelectTrigger>
              <SelectContent>
                {filters?.departments.map((dept) => (
                  <SelectItem key={dept} value={dept}>
                    {dept}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={requesterFilter} onValueChange={setRequesterFilter}>
              <SelectTrigger className="w-[180px] bg-gray-50 border-gray-200">
                <SelectValue placeholder="All Requesters" />
              </SelectTrigger>
              <SelectContent>
                {filters?.requesters.map((req) => (
                  <SelectItem key={req.id} value={req.name}>
                    {req.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode("table")}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === "table"
                    ? "bg-gray-900 text-white"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                Table
              </button>
              <button
                onClick={() => setViewMode("cards")}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === "cards"
                    ? "bg-gray-900 text-white"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                Cards
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Requests Section */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">
            Onboarding Requests ({filteredRequests.length})
          </h2>
          <p className="text-sm text-gray-500">Manage and track all onboarding requests</p>
        </div>

        {viewMode === "table" ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50/50">
                  <TableHead className="font-semibold text-gray-700">Employee</TableHead>
                  <TableHead className="font-semibold text-gray-700">Job Role</TableHead>
                  <TableHead className="font-semibold text-gray-700">Manager</TableHead>
                  <TableHead className="font-semibold text-gray-700">Progress %</TableHead>
                  <TableHead className="font-semibold text-gray-700">Forms</TableHead>
                  <TableHead className="font-semibold text-gray-700">Policies</TableHead>
                  <TableHead className="font-semibold text-gray-700">Training</TableHead>
                  <TableHead className="font-semibold text-gray-700">
                    <button
                      type="button"
                      onClick={toggleStatusSort}
                      className="flex items-center gap-1 hover:text-gray-900"
                      title="Sort by status"
                    >
                      Status
                      {statusSort === "asc" ? (
                        <ArrowUp className="w-3.5 h-3.5" />
                      ) : statusSort === "desc" ? (
                        <ArrowDown className="w-3.5 h-3.5" />
                      ) : (
                        <ArrowUpDown className="w-3.5 h-3.5 text-gray-400" />
                      )}
                    </button>
                  </TableHead>
                  <TableHead className="font-semibold text-gray-700">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-12 text-gray-500">
                      No onboarding requests found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRequests.map((request) => (
                    <TableRow key={request.id} className="hover:bg-gray-50/50">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-gray-900 text-white flex items-center justify-center text-sm font-semibold">
                            {getInitials(request.firstName, request.lastName)}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {request.firstName} {request.lastName}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatDate(request.startDate)}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-700">
                        {request.jobRoleName ?? request.jobPosition?.jobRole?.roleName ?? "—"}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-700">
                            {request.managerName ?? request.manager?.name ?? "—"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gray-900 rounded-full transition-all"
                              style={{ width: `${request.progress}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-gray-700">
                            {request.progress}%
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>{getItemStatusBadge(request.formsStatus)}</TableCell>
                      <TableCell>{getItemStatusBadge(request.policiesStatus)}</TableCell>
                      <TableCell>{getItemStatusBadge(request.trainingStatus)}</TableCell>
                      <TableCell>{getStatusBadge(request.status)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="gap-1 text-gray-600 hover:text-gray-900"
                            onClick={() => navigate(`/onboarding/${request.id}`)}
                            title="View"
                            aria-label="View"
                          >
                            <Eye className="w-4 h-4" />
                            
                          </Button>
                          {canConvert && request.status !== "CANCELLED" && (
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Resend Onboarding Link"
                              aria-label="Resend Onboarding Link"
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              disabled={resendingId === request.id}
                              onClick={() => handleResendInvite(request)}
                            >
                              {resendingId === request.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Send className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                          {canConvert && request.status === "COMPLETED" && (
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Convert to Active User"
                              aria-label="Convert to Active User"
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              disabled={convertingId === request.id}
                              onClick={() => handleConvertToUser(request)}
                            >
                              {convertingId === request.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <UserCheck className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                          {canConvert && request.status === "CANCELLED" && (
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Restore & Resend Onboarding Link"
                              aria-label="Restore & Resend Onboarding Link"
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              disabled={restoringId === request.id}
                              onClick={() => handleRestoreInvite(request)}
                            >
                              {restoringId === request.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <RotateCcw className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                          {canCancel &&
                            request.status !== "CANCELLED" &&
                            request.status !== "CONVERTED" && (
                              <Button
                                variant="ghost"
                                size="icon"
                                title="Cancel Request"
                                aria-label="Cancel Request"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                disabled={cancellingId === request.id}
                                onClick={() => setPendingCancel(request)}
                              >
                                {cancellingId === request.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <XCircle className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredRequests.length === 0 ? (
              <div className="col-span-full text-center py-12 text-gray-500">
                No onboarding requests found
              </div>
            ) : (
              filteredRequests.map((request) => (
                <div
                  key={request.id}
                  className="border border-gray-100 rounded-xl p-5 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/onboarding/${request.id}`)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gray-900 text-white flex items-center justify-center text-sm font-semibold">
                        {getInitials(request.firstName, request.lastName)}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">
                          {request.firstName} {request.lastName}
                        </p>
                        <p className="text-xs text-gray-500">
                          {request.jobRoleName ?? request.jobPosition?.jobRole?.roleName ?? "No job role"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {getStatusBadge(request.status)}
                      {canConvert && request.status !== "CANCELLED" && (
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Resend Onboarding Link"
                          aria-label="Resend Onboarding Link"
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 -mt-1"
                          disabled={resendingId === request.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleResendInvite(request);
                          }}
                        >
                          {resendingId === request.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Send className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                      {canConvert && request.status === "CANCELLED" && (
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Restore & Resend Onboarding Link"
                          aria-label="Restore & Resend Onboarding Link"
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 -mt-1"
                          disabled={restoringId === request.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRestoreInvite(request);
                          }}
                        >
                          {restoringId === request.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <RotateCcw className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                      {canCancel &&
                        request.status !== "CANCELLED" &&
                        request.status !== "CONVERTED" && (
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Cancel Request"
                            aria-label="Cancel Request"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 -mt-1"
                            disabled={cancellingId === request.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              setPendingCancel(request);
                            }}
                          >
                            {cancellingId === request.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <XCircle className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Progress</span>
                      <span className="font-medium">{request.progress}%</span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gray-900 rounded-full transition-all"
                        style={{ width: `${request.progress}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-sm pt-2">
                      <span className="text-gray-500">Manager</span>
                      <span className="text-gray-700">
                        {request.managerName ?? request.manager?.name ?? "—"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 pt-2">
                      {getItemStatusBadge(request.formsStatus)}
                      {getItemStatusBadge(request.policiesStatus)}
                      {getItemStatusBadge(request.trainingStatus)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
      {/* Cancel confirmation */}
      <AlertDialog
        open={!!pendingCancel}
        onOpenChange={(open) => {
          if (!open) setPendingCancel(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel onboarding request?</AlertDialogTitle>
            <AlertDialogDescription>
              This will cancel the onboarding request for{" "}
              <span className="font-medium">
                {pendingCancel?.firstName} {pendingCancel?.lastName}
              </span>{" "}
              and revoke their portal access — the hire will no longer be able
              to log in using their link. This action can be reversed by
              unarchiving the user in user management.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Request</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={cancellingId === pendingCancel?.id}
              onClick={(e) => {
                e.preventDefault();
                if (pendingCancel) handleCancel(pendingCancel);
              }}
            >
              {cancellingId === pendingCancel?.id ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Cancelling...
                </>
              ) : (
                "Cancel Request"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OnboardingDashboard;
